import getPersonaPhotoUrl from 'owa-persona/lib/utils/getPersonaPhotoUrl';
import type { Middleware, MiddlewareAPI, Dispatch } from 'redux';
import type { Action, UnifiedGroup } from 'people/lib/shared';
import {
    followGroupCompletedAction,
    getGroupDetailsData,
    unFollowGroupCompletedAction,
    renewGroupCompletedAction,
    joinGroupCompletedAction,
    leaveGroupCompletedAction,
} from 'people/lib/hub';
import type { LeaveGroupActionPayload } from 'people/lib/appstate';
import {
    editGroupCompletedAction,
    addGroupMembersCompletedAction,
    deleteGroupCompletedAction,
    memberManagementCompletedAction,
} from 'people/lib/editors/actions';
import type { HostAppProvidedPersonaIdentifiers } from 'owa-people-loki/lib/models/models';
import { isFeatureEnabled } from 'owa-feature-flags';

export declare interface GroupSubscriptionUpdateData {
    personaId: HostAppProvidedPersonaIdentifiers;
    subscriptionState: boolean;
}

export declare interface GroupUpdateData extends HostAppProvidedPersonaIdentifiers {
    photoUrl?: string;
}

export declare interface GroupUpdateSmtpData extends GroupUpdateData {
    oldSmtp?: string;
}

export declare type DataUpdateListener<T> = (newData: T) => void;

let lpcGroupSubscriptionUpdateListener: DataUpdateListener<GroupSubscriptionUpdateData>;

let lpcGroupDataUpdateListener: DataUpdateListener<GroupUpdateData>;

let lpcGroupMembersUpdateListener: DataUpdateListener<HostAppProvidedPersonaIdentifiers>;
let lpcGroupRenewalUpdateListener: DataUpdateListener<HostAppProvidedPersonaIdentifiers>;
let lpcGroupDeleteListener: DataUpdateListener<HostAppProvidedPersonaIdentifiers>;
let lpcGroupUpdateSmtpListener: DataUpdateListener<GroupUpdateSmtpData>;

export const setLpcGroupSubscriptionUpdateListener = (
    listener: DataUpdateListener<GroupSubscriptionUpdateData>
): void => {
    lpcGroupSubscriptionUpdateListener = listener;
};

export const setLpcGroupDataUpdateListener = (
    listener: DataUpdateListener<GroupUpdateData>
): void => {
    lpcGroupDataUpdateListener = listener;
};

export const setLpcGroupMembersDataUpdateListener = (
    listener: DataUpdateListener<HostAppProvidedPersonaIdentifiers>
): void => {
    lpcGroupMembersUpdateListener = listener;
};

export const setLpcGroupRenewalUpdateListener = (
    listener: DataUpdateListener<HostAppProvidedPersonaIdentifiers>
): void => {
    lpcGroupRenewalUpdateListener = listener;
};

export const setLpcGroupDeleteListener = (
    listener: DataUpdateListener<HostAppProvidedPersonaIdentifiers>
): void => {
    lpcGroupDeleteListener = listener;
};

export const setLpcGroupUpdateSmtpListener = (
    listener: DataUpdateListener<GroupUpdateSmtpData>
): void => {
    lpcGroupUpdateSmtpListener = listener;
};

const middleware = (_store: MiddlewareAPI) => (next: Dispatch) => (action: Action<{}>) => {
    const result = next(action);
    if (
        action.type === followGroupCompletedAction.success.toString() ||
        action.type === unFollowGroupCompletedAction.success.toString()
    ) {
        const payload = (action as Action<{
            groupId: string;
            groupSmtp: string;
            isFollowed: boolean;
        }>).payload;
        lpcUpdateGroupSubscription(payload.groupSmtp, payload.isFollowed);
    } else if (action.type === editGroupCompletedAction.toString()) {
        const payload = (action as Action<{
            groupId: string;
            tenantId: string;
            groupSmtp: string;
            photoUrl: string;
            hubPersonaId: string;
            oldGroupSmtp: string;
        }>).payload;
        getGroupDetailsData(next)(
            payload.groupId,
            payload.groupSmtp,
            payload.hubPersonaId,
            payload.tenantId
        );
        if (
            isFeatureEnabled('grp-cardReloadAfterEditingSmtp') &&
            payload.groupSmtp != payload.oldGroupSmtp
        ) {
            lpcUpdateGroupSmtpData(
                payload.groupSmtp,
                payload.groupId,
                payload.photoUrl,
                payload.oldGroupSmtp
            );
        } else {
            lpcUpdateGroupData(payload.groupSmtp, payload.photoUrl);
        }

        // Trigger re-fetching of cached photos displayed by the PersonaControl which is used throughout the hub
        import(/*RefreshPersonaPhoto*/ 'owa-persona/lib/actions/refreshPersonaPhoto')
            .then(module => module.default)
            .then(refreshPersonaPhoto => refreshPersonaPhoto(payload.groupSmtp));
        if (payload.photoUrl) {
            // Manually trigger refetching of the large photo used by EV/Contact editor
            fetch(getPersonaPhotoUrl(payload.groupSmtp, payload.groupId, 'GroupMailbox', true), {
                headers: { 'Cache-Control': 'no-cache' },
                credentials: 'include',
            });
        }
    } else if (action.type === addGroupMembersCompletedAction.toString()) {
        const payload = (action as Action<{
            groupId: string;
            groupSmtp: string;
            hubPersonaId: string;
        }>).payload;
        getGroupDetailsData(next)(payload.groupId, payload.groupSmtp, payload.hubPersonaId);
        lpcUpdateGroupMembersData(payload.groupSmtp);
    } else if (action.type === renewGroupCompletedAction.success.toString() && !action.error) {
        const payload = (action as Action<{ groupId: string; groupSmtp: string }>).payload;
        lpcRenewGroup(payload.groupSmtp);
    } else if (action.type === joinGroupCompletedAction.success.toString() && !action.error) {
        const payload = (action as Action<{ group: UnifiedGroup; userEmailAddress: string }>)
            .payload;
        lpcUpdateGroupMembersData(payload.group.emailAddress);
    } else if (action.type === leaveGroupCompletedAction.success.toString() && !action.error) {
        const payload = (action as Action<LeaveGroupActionPayload>).payload;
        lpcUpdateGroupMembersData(payload.groupSmtp);
    } else if (action.type === deleteGroupCompletedAction.toString()) {
        const payload = (action as Action<{
            hubPersonaId: string;
            groupId: string;
            groupEmailAddress: string;
            mailboxGuid: string;
            nextGroupInList: string;
        }>).payload;
        lpcDeleteGroup(payload.groupId, payload.groupEmailAddress);
    } else if (action.type === memberManagementCompletedAction.toString()) {
        const payload = (action as Action<{
            groupId: string;
            groupEmailAddress: string;
            hubPersonaId: string;
        }>).payload;
        getGroupDetailsData(next)(payload.groupId, payload.groupEmailAddress, payload.hubPersonaId);
        lpcUpdateGroupMembersData(payload.groupEmailAddress);
    }

    return result;
};

export const lpcUpdateGroupSubscription = (groupSmtp: string, subscriptionState: boolean) => {
    if (lpcGroupSubscriptionUpdateListener) {
        lpcGroupSubscriptionUpdateListener({
            personaId: {
                Smtp: groupSmtp,
                AadObjectId: '',
                PersonaType: 'Group',
                OlsPersonaId: '',
            },
            subscriptionState,
        });
    }
};

export const lpcUpdateGroupData = (groupSmtp: string, photoUrl: string) => {
    if (lpcGroupDataUpdateListener) {
        lpcGroupDataUpdateListener({
            Smtp: groupSmtp,
            AadObjectId: '',
            PersonaType: 'Group',
            OlsPersonaId: '',
            photoUrl: photoUrl,
        });
    }
};

export const lpcUpdateGroupMembersData = (groupSmtp: string) => {
    if (lpcGroupMembersUpdateListener) {
        lpcGroupMembersUpdateListener({
            Smtp: groupSmtp,
            AadObjectId: '',
            PersonaType: 'Group',
            OlsPersonaId: '',
        });
    }
};

export const lpcRenewGroup = (groupSmtp: string) => {
    if (lpcGroupRenewalUpdateListener) {
        lpcGroupRenewalUpdateListener({
            Smtp: groupSmtp,
            AadObjectId: '',
            PersonaType: 'Group',
            OlsPersonaId: '',
        });
    }
};

const lpcDeleteGroup = (groupId: string, groupEmailAddress: string) => {
    if (lpcGroupDeleteListener) {
        lpcGroupDeleteListener({
            Smtp: groupEmailAddress,
            AadObjectId: groupId,
            PersonaType: 'Group',
            OlsPersonaId: '',
        });
    }
};

export const lpcUpdateGroupSmtpData = (
    groupSmtp: string,
    aadObjectId: string,
    photoUrl: string,
    oldGroupSmtp: string
) => {
    if (lpcGroupUpdateSmtpListener) {
        lpcGroupUpdateSmtpListener({
            Smtp: groupSmtp,
            AadObjectId: aadObjectId,
            PersonaType: 'Group',
            OlsPersonaId: '',
            photoUrl: photoUrl,
            oldSmtp: oldGroupSmtp,
        });
    }
};

export const lpcDataUpdateMiddleware = middleware as Middleware;
